import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { useAuth } from '../hooks/useAuth'
import { getBelt } from '../lib/helpers'

export default function ParentPage() {
  const { user, signOut } = useAuth()
  const navigate = useNavigate()
  const [children, setChildren] = useState([]) // linked fighter profiles
  const [mode, setMode] = useState('view') // 'view' | 'link' | 'signup'
  const [linkCode, setLinkCode] = useState('') // passport ID to link
  const [linkError, setLinkError] = useState('')
  const [linking, setLinking] = useState(false)
  const [loading, setLoading] = useState(true)
  const [selectedChild, setSelectedChild] = useState(null)

  // Signup form
  const [form, setForm] = useState({ email: '', password: '', name: '' })
  const [signupError, setSignupError] = useState('')
  const [signingUp, setSigningUp] = useState(false)

  useEffect(() => {
    if (user) loadChildren()
    else { setLoading(false) }
  }, [user])

  async function loadChildren() {
    const { data: links } = await supabase
      .from('parent_links')
      .select('*, fighters(*)')
      .eq('parent_user_id', user.id)
    setChildren(links || [])
    if (links?.length) setSelectedChild(links[0].fighters)
    setLoading(false)
  }

  async function handleSignup(e) {
    e.preventDefault()
    if (!form.email || !form.password || !form.name) { setSignupError('Please fill in all fields'); return }
    setSigningUp(true); setSignupError('')
    const { data, error } = await supabase.auth.signUp({ email: form.email, password: form.password })
    if (error) { setSignupError(error.message); setSigningUp(false); return }
    await supabase.from('profiles').insert({ id: data.user.id, role: 'parent', full_name: form.name })
    setSigningUp(false)
    setMode('link')
  }

  async function linkChild() {
    if (!linkCode.trim()) return
    setLinking(true); setLinkError('')

    // Find fighter by passport ID
    const { data: fighter } = await supabase
      .from('fighters').select('*').eq('passport_id', linkCode.trim().toUpperCase()).single()

    if (!fighter) { setLinkError('Passport ID not found. Check the ID and try again.'); setLinking(false); return }

    // Check not already linked
    const { data: existing } = await supabase
      .from('parent_links').select('*').eq('parent_user_id', user.id).eq('fighter_id', fighter.id).single()

    if (existing) { setLinkError('This fighter is already linked to your account.'); setLinking(false); return }

    await supabase.from('parent_links').insert({ parent_user_id: user.id, fighter_id: fighter.id })
    setLinkCode('')
    setLinking(false)
    setMode('view')
    loadChildren()
  }

  if (loading) return <Shell><div style={{ color: '#888', textAlign: 'center', padding: '2rem' }}>Loading...</div></Shell>

  // Not logged in — show signup
  if (!user) {
    return (
      <Shell>
        <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
          <div style={{ fontSize: 40, marginBottom: 8 }}>👨‍👧</div>
          <div style={{ fontSize: 18, fontWeight: 600, color: '#1a1a1a', marginBottom: 4 }}>Parent account</div>
          <div style={{ fontSize: 13, color: '#888' }}>Follow your child's progress and get notified of milestones</div>
        </div>

        <div style={{ background: '#fff', borderRadius: 12, padding: '1.5rem', border: '1px solid #eee' }}>
          <form onSubmit={handleSignup}>
            <div style={{ marginBottom: 12 }}>
              <label style={labelStyle}>Your name</label>
              <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Ana Horvat" style={inputStyle} />
            </div>
            <div style={{ marginBottom: 12 }}>
              <label style={labelStyle}>Your email</label>
              <input type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} placeholder="ana@example.com" style={inputStyle} />
            </div>
            <div style={{ marginBottom: 16 }}>
              <label style={labelStyle}>Password</label>
              <input type="password" value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} placeholder="••••••••" style={inputStyle} />
            </div>
            {signupError && <div style={{ color: '#c00', fontSize: 12, marginBottom: 12, padding: '8px', background: '#fff0f0', borderRadius: 7 }}>{signupError}</div>}
            <button type="submit" disabled={signingUp} style={btnStyle}>
              {signingUp ? 'Creating account...' : 'Create parent account'}
            </button>
          </form>
          <div style={{ textAlign: 'center', marginTop: 14 }}>
            <button onClick={async () => {
              const email = prompt('Your email:')
              const password = prompt('Your password:')
              if (email && password) await supabase.auth.signInWithPassword({ email, password })
            }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#1860a8', fontSize: 13 }}>
              Already have an account? Log in
            </button>
          </div>
        </div>
      </Shell>
    )
  }

  // Logged in — link child flow
  if (mode === 'link') {
    return (
      <Shell onSignOut={signOut}>
        <div style={{ background: '#fff', borderRadius: 12, padding: '1.5rem', border: '1px solid #eee' }}>
          <div style={{ fontSize: 15, fontWeight: 600, color: '#1a1a1a', marginBottom: 6 }}>Link your child's profile</div>
          <div style={{ fontSize: 13, color: '#888', marginBottom: 16, lineHeight: 1.6 }}>
            Ask your child's coach for their Passport ID — it looks like <strong>FCP-A3B7C2D1</strong>. You'll find it at the bottom of the child's card page.
          </div>
          <label style={labelStyle}>Passport ID</label>
          <input
            value={linkCode} onChange={e => setLinkCode(e.target.value.toUpperCase())}
            placeholder="FCP-XXXXXXXX"
            style={{ ...inputStyle, fontFamily: 'monospace', fontSize: 16, letterSpacing: '0.1em', marginBottom: 10 }}
          />
          {linkError && <div style={{ color: '#c00', fontSize: 12, marginBottom: 10 }}>{linkError}</div>}
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={linkChild} disabled={linking} style={btnStyle}>{linking ? 'Linking...' : 'Link profile'}</button>
            <button onClick={() => setMode('view')} style={btnSecStyle}>Cancel</button>
          </div>
        </div>
      </Shell>
    )
  }

  // Main parent view
  return (
    <Shell onSignOut={signOut}>
      <style>{`@keyframes shine{0%{left:-80%}60%{left:130%}100%{left:130%}}`}</style>

      {children.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '3rem 1rem' }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>👨‍👧</div>
          <div style={{ fontSize: 16, fontWeight: 600, color: '#1a1a1a', marginBottom: 8 }}>No children linked yet</div>
          <div style={{ fontSize: 13, color: '#888', marginBottom: 20 }}>Link your child's fighter profile to follow their progress</div>
          <button onClick={() => setMode('link')} style={btnStyle}>Link a child's profile</button>
        </div>
      ) : (
        <>
          {/* Child selector if multiple */}
          {children.length > 1 && (
            <div style={{ display: 'flex', gap: 8, marginBottom: '1.5rem', overflowX: 'auto' }}>
              {children.map(link => (
                <button key={link.id} onClick={() => setSelectedChild(link.fighters)}
                  style={{ padding: '7px 14px', borderRadius: 20, border: '1px solid', fontFamily: 'inherit', fontSize: 13, cursor: 'pointer', whiteSpace: 'nowrap',
                    background: selectedChild?.id === link.fighters.id ? '#1860a8' : '#fff',
                    color: selectedChild?.id === link.fighters.id ? '#fff' : '#444',
                    borderColor: selectedChild?.id === link.fighters.id ? '#1860a8' : '#ddd'
                  }}>
                  {link.fighters.first_name}
                </button>
              ))}
            </div>
          )}

          {selectedChild && <ChildView fighter={selectedChild} />}

          <div style={{ textAlign: 'center', marginTop: '1.5rem' }}>
            <button onClick={() => setMode('link')} style={btnSecStyle}>+ Link another child</button>
          </div>
        </>
      )}
    </Shell>
  )
}

function ChildView({ fighter }) {
  const [skills, setSkills] = useState([])
  const [competitions, setCompetitions] = useState([])
  const [notifications, setNotifications] = useState([])

  useEffect(() => {
    async function load() {
      const [sk, comps, notifs] = await Promise.all([
        supabase.from('fighter_skills').select('*').eq('fighter_id', fighter.id),
        supabase.from('competitions').select('*').eq('fighter_id', fighter.id).order('competition_date', { ascending: false }),
        supabase.from('notifications').select('*').eq('fighter_id', fighter.id).order('sent_at', { ascending: false }).limit(10),
      ])
      setSkills(sk.data || [])
      setCompetitions(comps.data || [])
      setNotifications(notifs.data || [])
    }
    load()
  }, [fighter.id])

  const belt = getBelt(fighter)
  const goldCount = competitions.filter(c => c.medal === 'gold').length
  const silverCount = competitions.filter(c => c.medal === 'silver').length
  const bronzeCount = competitions.filter(c => c.medal === 'bronze').length

  return (
    <div>
      {/* Child header */}
      <div style={{ background: '#fff', borderRadius: 12, padding: '1rem 1.25rem', border: '1px solid #eee', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: 12 }}>
        <div style={{ width: 44, height: 44, borderRadius: '50%', background: '#eef4ff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 16, color: '#1860a8', flexShrink: 0 }}>
          {fighter.first_name[0]}{fighter.last_name[0]}
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 15, fontWeight: 600, color: '#1a1a1a' }}>{fighter.first_name} {fighter.last_name}</div>
          <div style={{ fontSize: 12, color: '#888' }}>{belt.n} belt · {fighter.type === 'kids' ? 'Kids' : 'Adult'}</div>
        </div>
      </div>

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 10, marginBottom: '1rem' }}>
        {[
          { label: 'Sessions', value: fighter.sessions_count || 0 },
          { label: 'Belt level', value: `Lv.${require('../lib/helpers').getBeltLevel(fighter)}` },
          { label: 'Medals', value: goldCount + silverCount + bronzeCount },
        ].map(s => (
          <div key={s.label} style={{ background: '#fff', borderRadius: 10, padding: 12, textAlign: 'center', border: '1px solid #eee' }}>
            <div style={{ fontSize: 20, fontWeight: 600, color: '#1a1a1a' }}>{s.value}</div>
            <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Recent notifications */}
      {notifications.length > 0 && (
        <div style={{ background: '#fff', borderRadius: 12, padding: '1rem 1.25rem', border: '1px solid #eee', marginBottom: '1rem' }}>
          <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 10, color: '#1a1a1a' }}>Recent milestones</div>
          {notifications.map(n => (
            <div key={n.id} style={{ display: 'flex', gap: 10, padding: '7px 0', borderBottom: '1px solid #f4f4f4' }}>
              <div style={{ fontSize: 16, flexShrink: 0 }}>
                {n.type === 'belt_promotion' ? '🏆' : n.type === 'competition' ? '🥇' : n.type === 'level_up' ? '⭐' : '🎯'}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#1a1a1a' }}>{n.title}</div>
                <div style={{ fontSize: 11, color: '#888' }}>{n.body}</div>
                <div style={{ fontSize: 10, color: '#bbb', marginTop: 2 }}>{new Date(n.sent_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Competition history */}
      {competitions.length > 0 && (
        <div style={{ background: '#fff', borderRadius: 12, padding: '1rem 1.25rem', border: '1px solid #eee' }}>
          <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 10, color: '#1a1a1a' }}>Competition history</div>
          {competitions.map((c, i) => {
            const col = c.medal === 'gold' ? '#d4a017' : c.medal === 'silver' ? '#909090' : '#a05020'
            return (
              <div key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 0', borderBottom: i < competitions.length - 1 ? '1px solid #f4f4f4' : 'none' }}>
                <div style={{ width: 12, height: 12, borderRadius: '50%', background: col, flexShrink: 0 }} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>{c.tournament_name}</div>
                  {c.submission_used && <div style={{ fontSize: 11, color: '#888' }}>via {c.submission_used}</div>}
                </div>
                <div style={{ fontSize: 12, fontWeight: 600, textTransform: 'capitalize' }}>{c.medal} · {c.wins}W</div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

function Shell({ children, onSignOut }) {
  return (
    <div style={{ minHeight: '100vh', background: '#f4f6fb', fontFamily: "'Segoe UI', system-ui, sans-serif" }}>
      <header style={{ background: '#fff', borderBottom: '1px solid #eee', padding: '0 1.25rem', height: 52, display: 'flex', alignItems: 'center', gap: 10, position: 'sticky', top: 0, zIndex: 100 }}>
        <span style={{ fontSize: 20 }}>🥋</span>
        <span style={{ fontWeight: 700, fontSize: 15, color: '#1a1a1a', flex: 1 }}>Parent view</span>
        {onSignOut && <button onClick={onSignOut} style={{ background: 'none', border: '1px solid #ddd', borderRadius: 8, padding: '5px 10px', fontSize: 12, color: '#888', cursor: 'pointer' }}>Sign out</button>}
      </header>
      <main style={{ maxWidth: 480, margin: '0 auto', padding: '1.5rem 1rem' }}>
        {children}
      </main>
    </div>
  )
}

const labelStyle = { display: 'block', fontSize: 12, color: '#666', fontWeight: 500, marginBottom: 5 }
const inputStyle = { width: '100%', padding: '9px 11px', borderRadius: 8, border: '1px solid #ddd', fontSize: 14, outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit' }
const btnStyle = { background: '#1860a8', color: '#fff', border: 'none', borderRadius: 9, padding: '10px 20px', fontSize: 14, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', width: '100%' }
const btnSecStyle = { background: 'transparent', color: '#444', border: '1px solid #ddd', borderRadius: 9, padding: '9px 20px', fontSize: 13, cursor: 'pointer', fontFamily: 'inherit' }
